// src/types/roles.ts
export type UserRole = 
  | 'admin'
  | 'principal' 
  | 'assistant_principal'
  | 'scheduling_coordinator'
  | 'head_of_department'
  | 'curriculum_leader'
  | 'teacher'
  | 'temp_teacher'
  | 'observer';

export type PermissionCategory = 
  | 'system_administration'
  | 'user_management'
  | 'grid_management'
  | 'staff_management'
  | 'curriculum_management'
  | 'reporting_analytics'
  | 'load_letters'
  | 'teaching_intentions'
  | 'settings_configuration';

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: PermissionCategory;
  level: 'view' | 'edit' | 'admin';
}

export interface RoleDefinition {
  id: UserRole;
  name: string;
  description: string;
  level: number; // 0 (lowest) to 10 (highest)
  permissions: string[];
  canManageRoles?: UserRole[]; // Which roles this role can manage
  accessScopes?: {
    global?: boolean;
    department?: boolean;
    curriculumArea?: boolean;
    own?: boolean;
  };
  features: {
    canViewAllGrids?: boolean;
    canEditGrids?: boolean;
    canPublishGrids?: boolean;
    canManageStaff?: boolean;
    canViewReports?: boolean;
    canGenerateLoadLetters?: boolean;
    canAccessSystemSettings?: boolean;
  };
}

// Define all available permissions
export const PERMISSIONS: Permission[] = [
  // System Administration
  {
    id: 'system_admin_full',
    name: 'Full System Administration',
    description: 'Complete access to all system functions and data',
    category: 'system_administration',
    level: 'admin'
  },
  {
    id: 'database_backup_restore',
    name: 'Database Backup & Restore',
    description: 'Can backup and restore system data',
    category: 'system_administration',
    level: 'admin'
  },
  {
    id: 'system_configuration',
    name: 'System Configuration',
    description: 'Can modify system-wide settings and configurations',
    category: 'system_administration',
    level: 'admin'
  },

  // User Management
  {
    id: 'user_management_full',
    name: 'Full User Management',
    description: 'Can create, edit, and delete all users',
    category: 'user_management',
    level: 'admin'
  },
  {
    id: 'user_roles_assign',
    name: 'Assign User Roles',
    description: 'Can assign and modify user roles',
    category: 'user_management',
    level: 'admin'
  },
  {
    id: 'user_view_all',
    name: 'View All Users',
    description: 'Can view all user information',
    category: 'user_management',
    level: 'view'
  },

  // Grid Management
  {
    id: 'grid_create_edit',
    name: 'Create & Edit Grids',
    description: 'Can create new grids and modify existing ones',
    category: 'grid_management',
    level: 'edit'
  },
  {
    id: 'grid_delete',
    name: 'Delete Grids',
    description: 'Can delete grids permanently',
    category: 'grid_management',
    level: 'admin'
  },
  {
    id: 'grid_publish',
    name: 'Publish Grids',
    description: 'Can publish grids to curriculum leaders',
    category: 'grid_management',
    level: 'edit'
  },
  {
    id: 'grid_view_all',
    name: 'View All Grids',
    description: 'Can view all grids across the system',
    category: 'grid_management',
    level: 'view'
  },
  {
    id: 'class_management',
    name: 'Class Management',
    description: 'Can add, edit, and remove individual classes',
    category: 'grid_management',
    level: 'edit'
  },
  {
    id: 'allocation_management',
    name: 'Teacher Allocation Management',
    description: 'Can assign and modify teacher allocations',
    category: 'grid_management',
    level: 'edit'
  },

  // Staff Management
  {
    id: 'staff_create_edit',
    name: 'Create & Edit Staff',
    description: 'Can create new staff and modify existing staff details',
    category: 'staff_management',
    level: 'edit'
  },
  {
    id: 'staff_delete',
    name: 'Delete Staff',
    description: 'Can delete staff records permanently',
    category: 'staff_management',
    level: 'admin'
  },
  {
    id: 'staff_allowances',
    name: 'Manage Staff Allowances',
    description: 'Can manage staff allowances and load calculations',
    category: 'staff_management',
    level: 'edit'
  },
  {
    id: 'staff_view_all',
    name: 'View All Staff',
    description: 'Can view all staff information',
    category: 'staff_management',
    level: 'view'
  },
  {
    id: 'staff_view_department',
    name: 'View Department Staff',
    description: 'Can view staff within own job function department (Teaching, HR, Admin, etc.)',
    category: 'staff_management',
    level: 'view'
  },

  // Curriculum Management
  {
    id: 'curriculum_preferences',
    name: 'Manage Curriculum Preferences',
    description: 'Can manage curriculum area preferences and requirements',
    category: 'curriculum_management',
    level: 'edit'
  },
  {
    id: 'curriculum_view_area',
    name: 'View Curriculum Area',
    description: 'Can view data for specific curriculum area (English, Maths, Science, HPE, etc.)',
    category: 'curriculum_management',
    level: 'view'
  },
  {
    id: 'curriculum_publish_view',
    name: 'View Published Curriculum Data',
    description: 'Can view published curriculum allocations',
    category: 'curriculum_management',
    level: 'view'
  },

  // Reporting & Analytics
  {
    id: 'reports_generate_all',
    name: 'Generate All Reports',
    description: 'Can generate any report across all areas',
    category: 'reporting_analytics',
    level: 'edit'
  },
  {
    id: 'reports_view_all',
    name: 'View All Reports',
    description: 'Can view any report across all areas',
    category: 'reporting_analytics',
    level: 'view'
  },
  {
    id: 'reports_department',
    name: 'Department Reports',
    description: 'Can view reports for own job function department (Teaching, HR, Admin, etc.)',
    category: 'reporting_analytics',
    level: 'view'
  },
  {
    id: 'reports_curriculum_area',
    name: 'Curriculum Area Reports',
    description: 'Can view reports for own curriculum area (English, Maths, Science, HPE, etc.)',
    category: 'reporting_analytics',
    level: 'view'
  },

  // Load Letters
  {
    id: 'load_letters_generate',
    name: 'Generate Load Letters',
    description: 'Can generate and finalize load letters',
    category: 'load_letters',
    level: 'edit'
  },
  {
    id: 'load_letters_view_all',
    name: 'View All Load Letters',
    description: 'Can view all staff load letters',
    category: 'load_letters',
    level: 'view'
  },
  {
    id: 'load_letters_view_own',
    name: 'View Own Load Letter',
    description: 'Can view own load letter only',
    category: 'load_letters',
    level: 'view'
  },

  // Teaching Intentions
  {
    id: 'intentions_manage_all',
    name: 'Manage All Teaching Intentions',
    description: 'Can manage teaching intentions for all staff',
    category: 'teaching_intentions',
    level: 'edit'
  },
  {
    id: 'intentions_view_all',
    name: 'View All Teaching Intentions',
    description: 'Can view teaching intentions for all staff',
    category: 'teaching_intentions',
    level: 'view'
  },
  {
    id: 'intentions_manage_own',
    name: 'Manage Own Teaching Intentions',
    description: 'Can manage own teaching intentions',
    category: 'teaching_intentions',
    level: 'edit'
  },

  // Settings & Configuration
  {
    id: 'school_configuration',
    name: 'School Configuration',
    description: 'Can modify school-wide configuration settings',
    category: 'settings_configuration',
    level: 'admin'
  },
  {
    id: 'department_management',
    name: 'Department Management',
    description: 'Can create and modify job function departments (Teaching, HR, Admin, etc.)',
    category: 'settings_configuration',
    level: 'admin'
  },
  {
    id: 'template_management',
    name: 'Template Management',
    description: 'Can manage grid templates and allowance templates',
    category: 'settings_configuration',
    level: 'edit'
  }
];

// Define role hierarchy and permissions
export const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    id: 'admin',
    name: 'System Administrator',
    description: 'Full system access with all permissions',
    level: 10,
    permissions: ['system_admin_full'], // Grants access to everything
    features: {
      canViewAllGrids: true,
      canEditGrids: true,
      canPublishGrids: true,
      canManageStaff: true,
      canViewReports: true,
      canGenerateLoadLetters: true,
      canAccessSystemSettings: true
    },
    accessScopes: { global: true }
  },
  {
    id: 'principal',
    name: 'Principal',
    description: 'School leadership with comprehensive access',
    level: 9,
    permissions: [
      'user_management_full', 'grid_view_all', 'grid_create_edit', 'grid_publish',
      'staff_view_all', 'staff_create_edit', 'staff_allowances',
      'reports_generate_all', 'load_letters_generate', 'intentions_view_all',
      'school_configuration', 'department_management'
    ],
    canManageRoles: ['assistant_principal', 'scheduling_coordinator', 'head_of_department', 'curriculum_leader', 'teacher', 'temp_teacher'],
    features: {
      canViewAllGrids: true,
      canEditGrids: true,
      canPublishGrids: true,
      canManageStaff: true,
      canViewReports: true,
      canGenerateLoadLetters: true,
      canAccessSystemSettings: true
    },
    accessScopes: { global: true }
  },
  {
    id: 'assistant_principal',
    name: 'Assistant Principal',
    description: 'Senior leadership with operational management access',
    level: 8,
    permissions: [
      'grid_view_all', 'grid_create_edit', 'grid_publish', 'class_management', 'allocation_management',
      'staff_view_all', 'staff_allowances', 'reports_generate_all', 'load_letters_generate',
      'intentions_view_all', 'template_management'
    ],
    canManageRoles: ['scheduling_coordinator', 'head_of_department', 'curriculum_leader', 'teacher', 'temp_teacher'],
    features: {
      canViewAllGrids: true,
      canEditGrids: true,
      canPublishGrids: true,
      canManageStaff: true,
      canViewReports: true,
      canGenerateLoadLetters: true,
      canAccessSystemSettings: false
    },
    accessScopes: { global: true }
  },
  {
    id: 'scheduling_coordinator',
    name: 'Scheduling Coordinator',
    description: 'Specialized role for timetabling and scheduling operations',
    level: 7,
    permissions: [
      'grid_view_all', 'grid_create_edit', 'grid_publish', 'class_management', 'allocation_management',
      'staff_view_all', 'reports_view_all', 'intentions_view_all'
    ],
    features: {
      canViewAllGrids: true,
      canEditGrids: true,
      canPublishGrids: true,
      canManageStaff: false,
      canViewReports: true,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { global: true }
  },
  {
    id: 'head_of_department',
    name: 'Head of Department',
    description: 'Department leadership managing job functions within the college (e.g., Teaching Department, HR, Administration)',
    level: 6,
    permissions: [
      'grid_view_all', 'staff_view_department', 'staff_create_edit',
      'reports_department', 'intentions_manage_all', 'curriculum_view_area'
    ],
    canManageRoles: ['teacher', 'temp_teacher'],
    features: {
      canViewAllGrids: true,
      canEditGrids: false,
      canPublishGrids: false,
      canManageStaff: false,
      canViewReports: true,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { department: true }
  },
  {
    id: 'curriculum_leader',
    name: 'Curriculum Leader',
    description: 'Manages teachers within a specific curriculum area (e.g., English, Mathematics, Science, HPE)',
    level: 5,
    permissions: [
      'curriculum_preferences', 'curriculum_view_area', 'curriculum_publish_view',
      'staff_view_department', 'reports_curriculum_area', 'intentions_view_all'
    ],
    features: {
      canViewAllGrids: false,
      canEditGrids: false,
      canPublishGrids: false,
      canManageStaff: false,
      canViewReports: true,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { curriculumArea: true }
  },
  {
    id: 'teacher',
    name: 'Teacher',
    description: 'Standard teaching staff with personal access',
    level: 3,
    permissions: [
      'load_letters_view_own', 'intentions_manage_own', 'curriculum_publish_view'
    ],
    features: {
      canViewAllGrids: false,
      canEditGrids: false,
      canPublishGrids: false,
      canManageStaff: false,
      canViewReports: false,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { own: true }
  },
  {
    id: 'temp_teacher',
    name: 'Temporary Teacher',
    description: 'Temporary teaching staff with limited access',
    level: 2,
    permissions: [
      'load_letters_view_own', 'intentions_manage_own'
    ],
    features: {
      canViewAllGrids: false,
      canEditGrids: false,
      canPublishGrids: false,
      canManageStaff: false,
      canViewReports: false,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { own: true }
  },
  {
    id: 'observer',
    name: 'Observer',
    description: 'Read-only access for external stakeholders',
    level: 1,
    permissions: [],
    features: {
      canViewAllGrids: false,
      canEditGrids: false,
      canPublishGrids: false,
      canManageStaff: false,
      canViewReports: false,
      canGenerateLoadLetters: false,
      canAccessSystemSettings: false
    },
    accessScopes: { own: true }
  }
];

// Helper functions
export const getRoleDefinition = (roleId: UserRole): RoleDefinition | undefined => {
  return ROLE_DEFINITIONS.find(role => role.id === roleId);
};

export const hasPermission = (userRole: UserRole, permissionId: string): boolean => {
  const role = getRoleDefinition(userRole);
  if (!role) return false;
  
  // Admin has access to everything
  if (role.permissions.includes('system_admin_full')) return true;
  
  return role.permissions.includes(permissionId);
};

export const canManageRole = (managerRole: UserRole, targetRole: UserRole): boolean => {
  const manager = getRoleDefinition(managerRole);
  if (!manager) return false;
  
  // Admin can manage anyone
  if (manager.permissions.includes('system_admin_full')) return true;
  
  const manager_canManageRoles_safe = manager.canManageRoles;
  return (Array.isArray(manager_canManageRoles_safe) && manager_canManageRoles_safe.includes(targetRole)) ?? false;
};

export const getRolesByCategory = (category: 'leadership' | 'academic' | 'operational'): RoleDefinition[] => {
  switch (category) {
    case 'leadership':
      return ROLE_DEFINITIONS.filter(role => 
        ['admin', 'principal', 'assistant_principal'].includes(role.id)
      );
    case 'academic':
      return ROLE_DEFINITIONS.filter(role => 
        ['head_of_department', 'curriculum_leader', 'teacher', 'temp_teacher'].includes(role.id)
      );
    case 'operational':
      return ROLE_DEFINITIONS.filter(role => 
        ['scheduling_coordinator', 'observer'].includes(role.id)
      );
    default:
      return ROLE_DEFINITIONS;
  }
};

export const getPermissionsByCategory = (category: PermissionCategory): Permission[] => {
  return PERMISSIONS.filter(permission => permission.category === category);
};